import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IPlotTemperatureRecord } from "@interfaces/IPlot";
import { TemperatureProbeCategory } from "@temperature/_enums/temperature/TemperatureProbeCategory.enum";
import { ITemperatureProbe, ITemperatureProbeWithLatestRecord, ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import { IZoneWithProbes } from "@temperature/_models/temperature/ITemperatureZone";
import { IVehicleWithTemperatureData } from "@temperature/_models/temperature/IVehicleWithTemperatureData";
import { Observable } from "rxjs";

@Injectable({ providedIn: 'root' })
export class TemperatureProbeService {
    constructor(private readonly http: HttpClient) { }

    getTemperatureProbes(): Observable<ITemperatureProbe[]> {
        return this.http.get<ITemperatureProbe[]>(`/api/temperature/probes/`);
    }

    getTemperatureProbesForVehicle(vehicleId: number): Observable<ITemperatureProbe[]> {
        return this.http.get<ITemperatureProbe[]>(`/api/temperature/probes/${vehicleId}`);
    }

    updateVehicleProbe(probe: ITemperatureProbe): Observable<ITemperatureProbe> {
        return this.http.post<ITemperatureProbe>(`/api/temperature/probes`, { body: probe });
    }

    getAmbientTemperature(probes: ITemperatureProbeWithLatestRecord[]): number {
        if (!probes || !probes.length) {
            return;
        }
        const ambientProbe = probes.find(probe => probe.Category === TemperatureProbeCategory.Ambient);
        if (ambientProbe) {
            return ambientProbe.latestRecord?.Temperature;
        }
    }

    mapProbesLatestRecord(zones: IZoneWithProbes[] = [], temperatureRecords: ITemperatureRecord[] = []): void {
        zones.forEach(zone => {
            if (!zone.probes.length) {
                return;
            }
            zone.probes.forEach((probe, index) => {
                this.mapProbeLatestRecord(probe, temperatureRecords);
            })
        })
    }

    mapProbeLatestRecord(probe: ITemperatureProbeWithTemperatureData, temperatureRecords: ITemperatureRecord[]) {
        probe.latestRecord = temperatureRecords.find(rec => rec.TemperatureProbeId === probe.Id);
    }

    mapTemperatureRecordsToProbes(probes: ITemperatureProbeWithTemperatureData[], temperatureRecords: IPlotTemperatureRecord[] | ITemperatureRecord[]): void {
        probes.forEach(probe => {
            probe.records = [];
            probe.records = temperatureRecords.filter(record => record.TemperatureProbeId === probe.Id);
        })
    }

    mapTemperatureProbesToVehicles(probes: ITemperatureProbe[], vehicles: IVehicleWithTemperatureData[]): void {
        if (!probes || !vehicles) {
            return;
        }

        probes.forEach(probe => {
            const vehicle = vehicles.find(vehicle => vehicle.Id === probe.VehicleId);

            if (!vehicle) {
                return;
            }

            if (!vehicle.temperatureProbes?.length) {
                vehicle.temperatureProbes = [];
            }
            vehicle.temperatureProbes.push(probe);
        })
    }

    cleanVehicleProbeName(vehicleReg: string, probe: ITemperatureProbe) {
        try {
            var regEx = new RegExp(vehicleReg, "ig");
            var replaceMask = "";

            // Remove vehicle registration from probe name
            probe.cleanName = probe.Name.replace(regEx, replaceMask);

            // Remove all non-alphanumerics from start of strong (i.e. hypens)
            while (/[^a-zA-Z0-9]/.test(probe.cleanName[0]) && probe.cleanName) {
                probe.cleanName = probe.cleanName.slice(1).trim();
            }

            if (!probe.cleanName) {
                probe.cleanName = vehicleReg;
            }
        }
        catch (err) {
        }
    }

    getLastUpdatedForVehicleProbes(probes: ITemperatureProbeWithTemperatureData[]) {
        const dates = [];

        probes.forEach(probe => {
            probe.latestRecord && dates.push(new Date(probe.latestRecord?.Timestamp).getTime());
        })
        if (dates.length > 0) {
            return new Date(Math.max(...dates));
        }

        return null;
    }

    getProbeByType(probes: ITemperatureProbeWithTemperatureData[], category: TemperatureProbeCategory): ITemperatureProbeWithTemperatureData {
        const probe = probes.find(probe => probe.Category === category);

        return probe;
    }

    temperatureIsInBoundsAccordingToProfile(probe: ITemperatureProbeWithTemperatureData): boolean {
        const latestTemperature = probe.latestRecord.Temperature;
        const profile = probe.TemperatureProfile;

        if (!latestTemperature || !profile) {
            return false;
        }

        if (latestTemperature <= profile.UpperBound && latestTemperature >= profile.LowerBound) {
            return true;
        }

        return false;
    }
}
